import React from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "swiper/css";
import "swiper/css/navigation";
import ServicePageShell, {
  FaqList,
  RelatedServices,
  PeopleAlsoAsk,
  SectionWrap,
  SectionHeading,
  Timeline,
  buildServiceSchemas,
} from "../../components/ServicePageShell";
import ServiceInquiryForm from "../../components/ServiceInquiryForm";
import { createWebPageSchema } from "../../utils/seo";
import hero from "../../assets/img/Q.webp";
import aiAutomationHero from "../../assets/img/ai-automationhero.png";
import whatwedo from "../../assets/img/whatwedo.png";
import benefitsImg from "../../assets/img/benefits.png";
import howItWorksImg from "../../assets/img/how-it-works.png";
import faqImg from "../../assets/img/faq.png";
import useCasesImage from "../../assets/img/use-cases.jpeg";

gsap.registerPlugin(ScrollTrigger);

const pagePath = "/services/ai-automation";

const faqs = [
  {
    question: "What types of processes can AI automation improve?",
    answer:
      "Yes, AI automation works best on repetitive work that follows a pattern. We usually start with finance, support, sales, operations, and reporting because those teams deal with frequent handoffs, manual checks, and delays. When a workflow is repeatable and measurable, it is usually a strong candidate for automation.",
  },
  {
    question: "How long does an AI automation project take?",
    answer:
      "Usually, the first automation can go live in a few weeks if the scope is focused. Larger projects with multiple tools, approval steps, and edge cases take longer because we test the logic carefully. The exact timeline depends on integrations, internal review cycles, and how complex the existing process is.",
  },
  {
    question: "Can you connect with our existing software stack?",
    answer:
      "Yes, and that is usually the smartest approach. We connect automation to the CRM, ERP, helpdesk, inbox, spreadsheets, and cloud apps you already use so your team keeps working in familiar tools. That reduces change management and makes adoption much smoother.",
  },
  {
    question: "Is AI automation suitable for small and mid-sized businesses in India?",
    answer:
      "Absolutely. Small and mid-sized businesses in India often see the fastest return because they need to remove manual work without adding headcount. Automation helps lean teams move faster, reduce errors, and serve customers with the consistency that larger teams usually have.",
  },
  {
    question: "How do you measure the impact of automation?",
    answer:
      "We measure impact with business outcomes, not guesswork. Time saved, manual steps removed, error reduction, response speed, and cost savings are the core metrics we track. That gives you a clear view of whether the workflow is paying for itself and where it can improve next.",
  },
];

const peopleAlsoAsk = [
  {
    question: "What does QBITS9 do for AI automation projects?",
    answer:
      "QBITS9 designs and builds automation systems that reduce manual work across sales, operations, finance, and support. We map the workflow, connect the right tools, and make the process easier to run so your team can move faster with fewer errors.",
  },
  {
    question: "Which business workflow should we automate first?",
    answer:
      "You should usually start with the workflow that repeats the most and creates the most friction. That might be lead routing, invoice handling, customer support, or internal approvals. The best first project is the one that saves time quickly and is simple enough to validate early.",
  },
  {
    question: "Can AI automation help a small business in India save time?",
    answer:
      "Yes, it can save a meaningful amount of time even for a small team. When repetitive admin work is removed from daily operations, people can focus on sales, customer service, and delivery. That is often where the fastest business value appears.",
  },
  {
    question: "Do you build custom AI agents or just simple workflow automations?",
    answer:
      "We do both. Some businesses only need a tightly scoped workflow automation, while others benefit from AI agents that classify requests, summarize information, and route tasks intelligently. We recommend the simplest solution that still solves the real problem.",
  },
  {
    question: "How do you make automation reliable after launch?",
    answer:
      "We test the workflow against real cases, define fallback paths, and monitor the first runs closely. Reliability comes from good logic, clear exceptions, and the ability to refine the automation as your business changes. That is how the system stays useful long after launch.",
  },
];

const processSteps = [
  {
    label: "Discovery",
    title: "Understand the workflow",
    description:
      "We identify the manual tasks, tools, approvals, and data points that create bottlenecks today.",
  },
  {
    label: "Design",
    title: "Map the automation journey",
    description:
      "We define the logic, triggers, exceptions, and handoffs so the workflow is practical and predictable.",
  },
  {
    label: "Build",
    title: "Develop and connect systems",
    description:
      "We connect APIs, dashboards, AI agents, and business tools into one working flow.",
  },
  {
    label: "Deploy",
    title: "Launch and refine",
    description:
      "We deploy the automation, monitor the outcome, and improve the workflow as your business evolves.",
  },
];

const schemas = buildServiceSchemas({
  name: "AI Automation Services for Business in India",
  description:
    "QBITS9 builds AI automation services for business India, helping companies reduce manual work, improve accuracy, and scale faster.",
  path: pagePath,
  serviceType: "AI automation services for business India",
  faqs,
  howToSteps: processSteps,
  breadcrumbItems: [
    { name: "Home", path: "/" },
    { name: "Services", path: "/#services" },
    { name: "AI Automation", path: pagePath },
  ],
});

const pageSchemas = [
  ...schemas,
  createWebPageSchema({
    name: "AI Automation Services for Business in India",
    description:
      "QBITS9 builds AI automation services for business India, helping companies reduce manual work, improve accuracy, and scale faster.",
    path: pagePath,
    speakableSelectors: [
      ".ai-automation-hero-description",
      ".ai-automation-quick-answer",
      ".ai-automation-benefit-copy",
    ],
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Services", path: "/#services" },
      { name: "AI Automation", path: pagePath },
    ],
  }),
];

const heroStats = [
  {
    title: "Time saved",
    value: "Less manual work, faster turnarounds",
  },
  {
    title: "Process clarity",
    value: "Workflows mapped across teams and tools",
  },
  {
    title: "ROI focus",
    value: "Built to reduce operational cost",
  },
];

const useCases = [
  {
    badge: "IP",
    title: "Invoice Processing",
    description:
      "Automate invoice capture, approval routing, validation, and record updates so finance teams spend less time on repetitive checks.",
  },
  {
    badge: "CS",
    title: "Customer Support Bot",
    description:
      "Handle FAQs, ticket triage, and routine customer responses with an AI assistant that works around the clock.",
  },
  {
    badge: "LQ",
    title: "Lead Qualification",
    description:
      "Score incoming leads, filter low-quality enquiries, and route hot prospects to the right sales owner automatically.",
  },
  {
    badge: "IM",
    title: "Inventory Management",
    description:
      "Connect orders, stock levels, and alerts so procurement and operations teams can react before shortages impact sales.",
  },
];

const UseCaseCardGrid = ({ items }) => (
  <div className="grid gap-6 md:grid-cols-1 xl:grid-cols-2">
    {items.map((item) => {
      const [firstWord, ...restWords] = item.title.split(" ");
      const restTitle = restWords.join(" ");

      return (
        <article
          key={item.title}
          className="group flex h-full flex-col justify-between rounded-[30px] border border-slate-200 bg-white p-8 shadow-[0_16px_45px_rgba(0,0,0,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]"
        >
          <div className="space-y-6">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-lg font-semibold text-[#FF6B00]">
              {item.badge}
            </span>

            <h3 className="text-xl font-semibold text-slate-900 font-unbounded leading-tight">
              <span className="text-primary">{firstWord}</span>
              {restTitle ? ` ${restTitle}` : ""}
            </h3>
            <p className="text-sm leading-7 text-slate-600 font-poppins">
              {item.description}
            </p>
          </div>
        </article>
      );
    })}
  </div>
);

const benefits = [
  {
    badge: "⏱",
    title: "Save time",
    description:
      "Remove repetitive manual steps from daily operations and give your team more time for high-value work.",
  },
  {
    badge: "✓",
    title: "Reduce errors",
    description:
      "Standardized workflows reduce missed updates, duplicate entries, and approval mistakes.",
  },
  {
    badge: "↗",
    title: "Scale faster",
    description:
      "Add more volume without adding the same level of admin overhead.",
  },
  {
    badge: "₹",
    title: "Lower costs",
    description:
      "Automation can reduce rework, shrink manual dependency, and improve operational efficiency.",
  },
  {
    badge: "24",
    title: "24/7 operation",
    description:
      "Keep key processes running even when your team is offline or focused on other work.",
  },
  {
    badge: "⚙",
    title: "Custom workflows",
    description:
      "We shape automation around your process instead of forcing your process to fit generic software.",
  },
];

const relatedLinks = [
  {
    to: "/",
    kicker: "Home",
    title: "Back to QBITS9",
    description: "Return to the homepage and explore the core story, portfolio, and company overview.",
    cta: "Open home",
  },
  {
    to: "/services/web-mobile-development",
    kicker: "Next service",
    title: "Web & mobile apps",
    description: "Build product-ready web platforms, native-style mobile apps, and API ecosystems.",
    cta: "View service",
  },
  {
    to: "/services/saas-erp-development",
    kicker: "Platform work",
    title: "SaaS & ERP development",
    description: "Create secure multi-tenant platforms and internal systems around your business model.",
    cta: "View service",
  },
  {
    to: "/services/desktop-software",
    kicker: "Desktop tools",
    title: "Desktop software",
    description: "Build offline-ready desktop applications and internal tools for daily operations.",
    cta: "View service",
  },
  {
    to: "/contact",
    kicker: "CTA",
    title: "Start a project",
    description: "Send your requirements and get a tailored conversation started with our team.",
    cta: "Contact us",
  },
];

const AIAutomation = () => {
  const textSectionRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      const setupSplitAnimation = ({ pin, start, end }) => {
        const textElement = textRef.current;
        const sectionElement = textSectionRef.current;
        if (!textElement || !sectionElement) return undefined;

        const textContent = textElement.textContent;
        textElement.innerHTML = "";

        textContent.split("").forEach((char) => {
          const span = document.createElement("span");
          span.textContent = char;
          span.style.color = "#8C8C8C";
          textElement.appendChild(span);
        });

        const chars = textElement.querySelectorAll("span");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionElement,
            start,
            end,
            pin,
            scrub: 2,
            invalidateOnRefresh: true,
          },
        });

        tl.to(chars, {
          color: "#000000",
          stagger: 0.1,
          ease: "none",
        });

        return () => {
          tl.kill();
          textElement.textContent = textContent;
        };
      };

      mm.add("(min-width: 768px)", () =>
        setupSplitAnimation({
          pin: true,
          start: "top top",
          end: "+=150%",
        })
      );

      mm.add("(max-width: 767px)", () =>
        setupSplitAnimation({
          pin: true,
          start: "top top",
          end: "+=120%",
        })
      );
    });

    return () => ctx.revert();
  }, []);
  return (
    <ServicePageShell
      title="AI Automation Services for Business in India | QBITS9"
      description="QBITS9 builds AI automation services for business India, helping teams reduce manual work, lower operational cost, and scale faster with intelligent workflows."
      canonicalPath={pagePath}
      keywords="AI automation services for business India, business automation India, workflow automation, AI agents, process automation"
      schemas={pageSchemas}
    >
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 top-[4%] md:w-[350px] w-[280px]">
          <img data-aos="fade-right"
            src={hero}
            alt="AI automation services for businesses illustration"
            className="w-full h-auto aspect-auto"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="800"
            height="800"
          />
        </div>
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 lg:px-16">
          <nav className="flex flex-wrap items-center gap-2 lg:text-base text-sm font-[500] text-blue font-poppins lg:w-[65%] w-full px-5 lg:px-0">
            <Link to="/" className="hover:text-[#FF6B00]">
              Home
            </Link>
            <span>/</span>
            <span>Services</span>
            <span>/</span>
            <span className="text-slate-900">AI Automation</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-5">
                <span className="font-peachi text-[16px] md:text-[18px] text-black font-[500] text-black mb-3">
                  Surat, India · <span className="text-primary p-[8px] bg-[#ffbf7e1f] rounded-[30px]"> AI workflows for growing teams </span>{" "} 
                </span>
                <h1 data-aos="fade-up" data-aos-duration="500" data-aos-delay="200"
                  className="text-black xl:text-[58px] lg:text-[50px] md:text-[42px] text-[28px] font-bold leading-[1.3]"
                >
                  AI Automation Services For <span className="text-primary"> Businesses</span>{" "} In India That Cut Manual Work And Unlock Faster Growth.
                </h1>
                <p data-aos="fade-up" data-aos-delay="300" className="ai-automation-hero-description max-w-2xl text-lg leading-8 text-slate-600 font-poppins">
                  QBITS9 designs intelligent workflows, AI agents, and automation systems that reduce repetitive tasks, save operating cost, and give your team more time to focus on revenue work.
                </p>
              </div>

              <div data-aos="fade-up" data-aos-delay="400" className="flex flex-wrap gap-4">
                <HashLink
                  to="#service-inquiry"
                  smooth
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-primary px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group cursor-pointer"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">Get a Free Automation Audit</span>
                  <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E85D00] transition-all duration-500 group-hover:h-[25rem] group-hover:w-[25rem]" />
                </HashLink>
                <HashLink
                  to="/services/web-mobile-development"
                  className="w-fit relative overflow-hidden flex items-center justify-center bg-blue px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
                >
                  <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap"> See Web & Mobile Development</span>
                  <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[25rem] group-hover:h-[25rem] -translate-x-1/2 -translate-y-1/2" />
                </HashLink>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {heroStats.map((stat, index) => (
                  <article
                    key={stat.title}
                    data-aos="fade-up"
                    data-aos-delay={500 + (index * 100)}
                    className="h-full bg-white rounded-[22px] py-[18px] px-[18px] flex flex-col gap-2 shadow-[0_12px_30px_0_#0000000F] hover:shadow-[0_12px_30px_0_#00000030] transition duration-500 ease-in-out cursor-pointer"
                  >
                    <p className="text-[18px] font-bold text-primary">
                      {stat.title}
                    </p>
                    <p className="text-[15px] font-light text-lightBlack line-clamp-3">
                      {stat.value}
                    </p>
                  </article>
                ))}
              </div>
            </div>

            <div className="lg:pl-4">
              <div data-aos="fade-left" data-aos-delay="200" className="">
                <img className="rounded-[40px]" src={aiAutomationHero} alt="AI automation team reviewing a workflow dashboard in a modern business office." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TEXT SPLIT PIN SECTION ---------- */}
      <section
        ref={textSectionRef}
        className="text_animation min-h-screen lg:mt-[-150px] mt-0 flex items-center justify-center px-5 lg:py-0 py-[80px] w-full overflow-hidden"
      >
        <div
          aria-hidden="true"
          ref={textRef}
          data-aos="fade-up"
          className="font-peachi text-[#8C8C8C] text-[22px] md:text-[30px] lg:text-[38px] leading-[1.7] max-w-6xl text-center"
        >
          QBITS9 builds AI automation systems for businesses in India that remove repetitive work, connect the tools your team already uses, and turn manual workflows into faster, more reliable operations. The result is less admin, fewer errors, and more time for growth work.
        </div>
        <p className="sr-only">
          QBITS9 builds AI automation systems for businesses in India that remove repetitive work, connect the tools your team already uses, and turn manual workflows into faster, more reliable operations.
        </p>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div data-aos="fade-right" className="order-2 lg:order-1">
              <img 
                src={whatwedo} 
                alt="AI workflow automation process mapping"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6 lg:px-0 px-[20px]">
              <div className="space-y-3">
                <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-4 lg:mb-8 aos-init aos-animate">
                  <span className="text-primary" > What we do </span>
                </h3>
                <h2 data-aos="fade-up" data-aos-delay="100" className="text-[18px] md:text-[22px] lg:text-[32px] xl:text-[42px] font-unbounded font-bold text-lightBlack leading-[1.4] mb-[10px] md:mb-[20px] lg:mb-[40px] aos-init aos-animate">
                  AI workflow automation that reduces manual tasks
                </h2>
              </div>

              <p data-aos="fade-up" data-aos-delay="200" className="font-peachi text-sm md:text-base text-lightBlack aos-init aos-animate">
                We start by mapping the real work inside your company, then design automation around the parts that slow teams down the most. The goal is not to add another tool. The goal is to remove repetitive effort, create better visibility, and make every process easier to run.
              </p>
              <p data-aos="fade-up" data-aos-delay="250" className="font-peachi text-sm md:text-base text-lightBlack">
                If you want to see practical examples before committing, read <Link to="/blog/how-ai-automation-helps-small-businesses-in-india-save-10-hours-per-week" className="text-primary underline hover:text-secondary">how AI automation helps small businesses save time</Link> or review <Link to="/blog/ai-automation-use-cases-that-deliver-the-fastest-roi-in-india" className="text-primary underline hover:text-secondary">AI automation use cases with fast ROI</Link>.
              </p>

              <ul className="space-y-4 ai-automation-benefit-copy">
                <li data-aos="fade-up" data-aos-delay="300" className="flex gap-3 home-hero-description text-blue text-[17px] font-light font-poppins aos-init aos-animate">
                  <span className="text-primary font-bold text-lg flex-shrink-0">✓</span>
                  <span>AI automation is the use of intelligent software to handle recurring business tasks automatically. We analyse how information moves across sales, operations, finance, and support so we can remove bottlenecks.</span>
                </li>
                <li data-aos="fade-up" data-aos-delay="400" className="flex gap-3 home-hero-description text-blue text-[17px] font-light font-poppins aos-init aos-animate">
                  <span className="text-primary font-bold text-lg flex-shrink-0">✓</span>
                  <span>Once clear, we design workflows connecting your CRM, inbox, spreadsheet, ticketing system, ERP, or cloud platform into one reliable process.</span>
                </li>
                <li data-aos="fade-up" data-aos-delay="500" className="flex gap-3 home-hero-description text-blue text-[17px] font-light font-poppins aos-init aos-animate">
                  <span className="text-primary font-bold text-lg flex-shrink-0">✓</span>
                  <span>Workflows include AI classification, routing, automated follow-ups, document extraction, approval logic, and alerts. For Indian businesses, faster service and scalable operations without hiring overhead.</span>
                </li>
                <li data-aos="fade-up" data-aos-delay="600" className="flex gap-3 home-hero-description text-blue text-[17px] font-light font-poppins aos-init aos-animate">
                  <span className="text-primary font-bold text-lg flex-shrink-0">✓</span>
                  <span>After launch, we monitor and refine so automation continues to save time, reduce errors, and scale with your business.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] lg:items-center">
            <div className="space-y-8">
              <SectionHeading
                eyebrow="Use cases"
                title="Practical AI automation use cases for sales, ops, and support"
                description="We typically begin with high-volume, repetitive tasks that create friction every day. These are the jobs where workflow automation can create measurable savings fast."
              />
              <UseCaseCardGrid items={useCases} />
            </div>
            <div data-aos="fade-left" className="relative">
              <img
                src={useCasesImage}
                alt="AI automation use cases illustration"
                className="w-full h-auto rounded-[32px]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-20 relative">
        <div data-aos="fade-right" className="absolute inset-0 lg:top-[30%] top-[10%] md:w-[350px] w-[280px] -z-10 opacity-30">
          <img
            src={hero}
            alt="AI automation background"
            className="w-full h-auto aspect-auto"
            width="350"
            height="350"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-10 lg:items-start pt-10">
            <div className="lg:sticky lg:top-32 space-y-8">
              <SectionHeading
                eyebrow="Benefits"
                title="Why teams choose QBITS9 for AI automation consulting"
                description="Our systems are designed to deliver both operational and financial value, so automation helps your business move faster while staying lean."
              />
              <div data-aos="fade-up" data-aos-delay="200" className="mt-4">
                <img
                  src={benefitsImg}
                  alt="AI automation benefits illustration"
                  className="w-full h-auto rounded-[24px] lg:rounded-[32px] shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              {benefits.map((item, index) => (
                <article
                  key={item.title}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  className="rounded-[24px] lg:rounded-[32px] border border-slate-100 bg-white p-6 lg:p-8 flex flex-col justify-between shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_18px_50px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="space-y-6">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF3E9] text-lg font-semibold text-[#FF6B00]">
                      {item.badge}
                    </span>
                    <h3 className="text-xl font-bold text-slate-900 font-unbounded leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm leading-7 text-slate-600 font-poppins">
                      {item.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionWrap
        eyebrow="How it works"
        title="A clear four-step process from discovery to deployment"
        description="We keep the process practical and transparent so you always know what is being built, why it matters, and how the workflow will behave in production."
        image={howItWorksImg}
        imageAlt="How AI automation works"
        imagePosition="right"
      >
        <Timeline steps={processSteps} columns="md:grid-cols-2" />
      </SectionWrap>

      <SectionWrap
        eyebrow="FAQ"
        title="Frequently asked questions about AI automation"
        description="These are the questions business owners usually ask before starting an automation project."
        image={faqImg}
        imageAlt="Frequently asked questions about AI automation"
        imagePosition="left"
      >
        <FaqList faqs={faqs} />
      </SectionWrap>

      <SectionWrap
        eyebrow="CTA"
        title="Ready to automate your business?"
        description="These are the questions business owners usually ask before starting an automation project. Each answer is written to be clear, direct, and easy to cite."
        suggestion={{
          title: "Suggested CTA image",
          text: "Use a meeting scene with a founder and automation consultant reviewing a laptop dashboard, orange notebook, and workflow map on a bright white desk."
        }}
        rightContent={
          <div data-aos="fade-left">
            <ServiceInquiryForm
              title="Request your free automation review"
              description="Tell us what your team does today and where the time goes. We will reply with a practical starting point."
              submitLabel="Request an Automation Audit"
              serviceName="AI Automation"
              defaultSource="AI Automation page"
              sourceOptions={[
                "AI Automation page",
                "Referral",
                "Search",
                "Social media",
                "Other",
              ]}
              successTitle="Your automation enquiry is in"
              successDescription="Thanks for reaching out. We will review your automation goals and get back to you shortly."
            />
          </div>
        }
      >
        <div className="space-y-6" id="service-inquiry">
          <p className="text-sm leading-7 text-slate-600 font-poppins">
            Some businesses start with workflow automation only, while others need connected <Link to="/services/saas-erp-development" className="text-primary underline hover:text-secondary">SaaS and ERP systems</Link> or supporting <Link to="/services/web-mobile-development" className="text-primary underline hover:text-secondary">web and mobile apps</Link> to make the automation useful across the whole operation.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              "Reduce repetitive tasks across teams",
              "Connect existing tools and data sources",
              "Improve speed, visibility, and accuracy",
              "Start with a focused, high-ROI use case",
            ].map((item, index) => (
              <div
                key={item}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="rounded-[10px] border border-slate-200 bg-[#FFF9F4] px-4 py-3 text-[15px] font-light text-lightBlack line-clamp-3 transition-all duration-300 hover:border-primary hover:shadow-md"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </SectionWrap>

      <RelatedServices links={relatedLinks} variant="service" />

      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-16">
          <div className="mb-6 max-w-3xl">
             <h3 data-aos="fade-up" className="font-peachi text-[28px] font-bold text-black mb-2 lg:mb-4 aos-init aos-animate">
              <span className="text-primary" >People also ask</span>
            </h3>
            <h2 data-aos="fade-up" data-aos-delay="100" className="mt-3 text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
              More questions about AI automation for Indian businesses
            </h2>
          </div>
          <PeopleAlsoAsk items={peopleAlsoAsk} />
        </div>
      </section>
    </ServicePageShell>
  );
};

export default AIAutomation;
